import React, { useMemo } from 'react';
import {
  eventConditionsPrimitiveSelector,
  filterPrimitiveSelector,
} from '../../../../redux/selectors/builder';
import {
  FieldContext,
  IEventConditionFieldsFragment,
  IFilterFieldsFragment,
  TagOption,
} from '../../../../utils/apollo/graphqlSchema';
import { IRowMeta } from '../../../../redux/slices/oldBuilder';
import { MaybeNil } from '../../../../utils/interfaces';
import {
  BuilderFilterFieldsHighlighted,
  BuilderFilterFieldsTrigger,
} from '../../BuilderFields/BuilderFilterFields';
import { useAppSelector } from '../../../../redux/hooks';
import { useDndEntityInternalContext } from '../../../DndEntity';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../../DndEntity/utils';
import DndDroppableZone from '../../../DndEntity/DndDroppableZone';
import DropzoneArea from '../../../DndEntity/DndDroppableZone/DropzoneArea';
import {
  EntityDropzoneDataTypeEnum,
  IFieldEntityDropzoneDataType,
  INamedFilterEntityDropzoneDataType,
} from '../../../DndEntity/validation';
import { EntityDropzoneType } from '../../../QuickAccessDictionary/DictionaryTabs/types';
import { isNil } from 'lodash';
import FieldDroppableZone from '../../../DndEntity/DndDroppableZone/FieldDroppableZone';
import { defaultEventInput } from '../../../../utils/builder';
import { createTagOptionInput } from '../../EntitySelection/utils';
import { generateEventsInputByEvents } from '../../utils/builderUtils';

interface IProps {
  rowMeta: IRowMeta;
}

const BuilderCohortFilters = ({ rowMeta }: IProps) => {
  const filterSelector = useMemo(filterPrimitiveSelector, []);

  const filter: MaybeNil<IFilterFieldsFragment> = useAppSelector((state) =>
    filterSelector(state, rowMeta),
  );
  const eventConditionsSelector = useMemo(eventConditionsPrimitiveSelector, []);
  const eventConditions: IEventConditionFieldsFragment[] = useAppSelector(
    (state) => eventConditionsSelector(state, rowMeta),
  );
  const { activeItem } = useDndEntityInternalContext();

  const showAddFilterDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (!isNil(filter)) {
      return false;
    }

    return data && data.__typename === 'TagLeafFilter';
  }, [activeItem, filter]);

  const showAddFieldDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (!isNil(filter)) {
      return false;
    }

    return data && data.__typename === 'TagLeafField';
  }, [activeItem, filter]);

  const fieldsDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Fields,
    rowMeta,
  );

  const filtersDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Filters,
    rowMeta,
  );

  const tags = [
    createTagOptionInput(TagOption.Field, {
      field: {
        func: null,
        context: FieldContext.Filter,
        events: !isNil(rowMeta.eventIndex)
          ? generateEventsInputByEvents(
              eventConditions[rowMeta.eventIndex].events,
            )
          : [defaultEventInput],
        arrayObjectContext: null,
      },
    }),
  ];

  return (
    <div>
      {showAddFieldDropzone ? (
        <FieldDroppableZone
          dropzoneId={fieldsDropzoneId}
          tags={tags}
          data={
            {
              id: fieldsDropzoneId,
              type: EntityDropzoneDataTypeEnum.Fields,
              rowMeta,
            } satisfies IFieldEntityDropzoneDataType
          }
          hideOnDisabled
        >
          <DropzoneArea size="large">+ Add Event Filter</DropzoneArea>
        </FieldDroppableZone>
      ) : showAddFilterDropzone ? (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Filters]}
          id={filtersDropzoneId}
          data={
            {
              id: filtersDropzoneId,
              type: EntityDropzoneDataTypeEnum.Filters,
              rowMeta,
            } satisfies INamedFilterEntityDropzoneDataType
          }
        >
          <DropzoneArea size="large">+ Add Event Filter</DropzoneArea>
        </DndDroppableZone>
      ) : (
        <BuilderFilterFieldsTrigger
          rowMeta={rowMeta}
          fieldsDropzoneId={fieldsDropzoneId}
        />
      )}
      {filter?.conditions?.length && (
        <BuilderFilterFieldsHighlighted rowMeta={rowMeta} filter={filter} />
      )}
    </div>
  );
};

export default BuilderCohortFilters;
